import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { createStory } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Send, Shield, ArrowLeft } from "lucide-react";

const CATEGORIES = [
  { key: "HIV", label: "HIV", color: "border-red-200 bg-red-50 text-red-700" },
  { key: "HPV", label: "HPV", color: "border-blue-200 bg-blue-50 text-blue-700" },
  { key: "HSV", label: "HSV", color: "border-green-200 bg-green-50 text-green-700" },
];

export default function CreateStory() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [category, setCategory] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const isVerified = user?.verified;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isVerified) {
      toast.error("Only verified users can post stories.");
      return;
    }
    if (!content.trim() || !category) {
      toast.error("Please fill in all fields");
      return;
    }

    setSubmitting(true);
    try {
      await createStory({ title: title.trim(), content: content.trim(), category });
      toast.success("Story published!");
      navigate("/stories");
    } catch (err) {
      toast.error("Failed to publish story");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isVerified) {
    return (
      <div className="min-h-screen bg-[#FAFAF7]">
        <Navbar />
        <main className="max-w-lg mx-auto px-4 py-8">
          <button onClick={() => navigate("/stories")} className="flex items-center gap-1 text-sm text-[#6B6B70] hover:text-[#0F0F10] mb-6">
            <ArrowLeft className="w-4 h-4" /> Back to stories
          </button>
          <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-6 text-center">
            <Shield className="w-12 h-12 text-yellow-500 mx-auto mb-3" />
            <h2 className="font-display text-xl font-semibold mb-2">Verification Required</h2>
            <p className="text-sm text-gray-600 mb-4">
              Only verified users can create stories. Get verified in the shop to share your journey.
            </p>
            <Link to="/shop" className="neo-btn bg-yellow-500 border-yellow-500 text-sm">
              Go to Shop – 💎 10
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <Navbar />
      <main className="max-w-lg mx-auto px-4 py-8">
        <button onClick={() => navigate("/stories")} className="flex items-center gap-1 text-sm text-[#6B6B70] hover:text-[#0F0F10] mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to stories
        </button>
        <h1 className="font-display text-2xl font-semibold mb-6">Create a Story</h1>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-[#0F0F10]">Category</label>
            <div className="flex gap-2 mt-2">
              {CATEGORIES.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => setCategory(c.key)}
                  className={`px-4 py-2.5 rounded-xl border text-sm font-medium transition-colors ${
                    category === c.key ? c.color + " border-2" : "border-[#E7E5E0] bg-white text-[#0F0F10] hover:bg-[#F5F3EE]"
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-[#0F0F10]">Title (optional)</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Give your story a title..."
              className="neo-input mt-1"
              maxLength={100}
            />
          </div>

          <div>
            <label className="text-sm font-medium text-[#0F0F10]">Your Story</label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Share your experience..."
              className="neo-input mt-1 h-40 resize-none"
              maxLength={2000}
              required
            />
            <p className="text-xs text-[#6B6B70] mt-1">{content.length}/2000</p>
          </div>

          <button
            type="submit"
            disabled={submitting || !content.trim() || !category}
            className="neo-btn w-full bg-[#7C3AED] border-[#7C3AED]"
          >
            {submitting ? "Publishing..." : "Publish Story"} <Send className="w-4 h-4" />
          </button>
        </form>
      </main>
    </div>
  );
}